import { useState, Fragment, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import qs from 'qs'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGlobe } from '@fortawesome/pro-regular-svg-icons'
import { useInView } from 'react-intersection-observer'
import { Link } from '../typography/Link'
import { UnstyledLink } from '../typography/UnstyledLink'
import { Stack } from '../stack'
import { List } from '../list'
import { ScreenReaderText } from '../screen-reader-text'
import { Button } from '../button'
import { Container, Row, Col } from '../grid'
import { HamburgerMenu } from './HamburgerMenu'
import { SubNav } from './SubNav'
import { findTopLevelNavItem, checkShouldShowSubNav } from './utils'
import * as styles from './Header.module.scss'
import ChevronImg from '../../images-global/Chevron.png'
import { NavigationPanel } from './NavigationPanel/NavigationPanel'

const Header = ({ pageContext, location, menu, showSubnav, showSubnavOnScroll }) => {
  const [t, i18n] = useTranslation()

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
  const displaySearchResults = !!queryParams.q

  const [menuOpen, setMenuOpen] = useState(displaySearchResults ? 'search' : false)
  const openSearchMenu = () => setMenuOpen('search')
  const openHamburgerMenu = () => setMenuOpen('hamburger')
  const [showPersonalPanel, setPersonalPanel] = useState(false)
  const [showBusinessPanel, setBusinessPanel] = useState(false)
  const [isBusinessPanel, setIsBusinessPanel] = useState(false)
  const personalButtonRef = useRef(null)
  const businessButtonRef = useRef(null)
  const closeMenu = () => setMenuOpen(false)

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0,
  })

  const observerHasInitialized = Boolean(entry)
  /**
   * the code below check the users current url, if the user is on a business page it
   * then sets isBusiness to "business" which is appended to the url for the login
   * page the login page checks if the url contians "business" if it does it sets the
   * login to the business default login.
   */
  const isBusiness = location.pathname.includes('business')

  // Heads up! We use two different nav menus for sub nav and main menu
  const currentPage = findTopLevelNavItem(pageContext.pathname, menu.subnav)

  const shouldShowSubnav = checkShouldShowSubNav(
    showSubnav,
    showSubnavOnScroll,
    currentPage,
    observerHasInitialized,
    inView
  )

  const classes = classNames(
    showSubnavOnScroll ? styles.Header : styles.headerSticky,
    currentPage && ['/', '/business'].indexOf(currentPage.urlPath) !== -1 && styles.TransparentBg
  )

  const onclickPersonal = () => {
    setPersonalPanel(!showPersonalPanel)
  }

  const onClickBusiness = () => {
    setBusinessPanel(!showBusinessPanel)
  }

  const logoAriaLabel = 'Elevations Credit Union'

  return (
    <Fragment>
      <header role="banner" className={classes} ref={ref} data-qa="header">
        <Container className={styles.mainHeaderContainer}>
          <Row>
            <Col xs={12}>
              <Stack alignment="center" distribution="equalSpacing" wrap={false}>
                <UnstyledLink
                  to="/"
                  className={styles.HomeLink}
                  aria-label={logoAriaLabel}
                  data-qa="home-link"
                >
                  <svg
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 138 85"
                    className={styles.LogoMark}
                    aria-hidden="true"
                    focusable="false"
                  >
                    <title>{logoAriaLabel}</title>
                    <g fill="none" fillRule="nonzero">
                      <path
                        fill="#63B5E5"
                        d="M129.3 11.7c-22.5 17.4-48.4 59.4-52.7 70.5-.4 1 .4 1.9 1.4 2.2a13.3 13.3 0 0 0 3 .6 2.7 2.7 0 0 0 2.6-1.6c10-21.7 29.2-56 46.6-71.1.8-.6.2-1.5-.9-.6"
                      />
                      <path
                        fill="#84BD41"
                        d="M57.183 31.656a.401.401 0 0 1-.402-.402l.2-9.138c0-.2.202-.301.402-.301s29.093 4.72 36.918 5.623h.401A187.603 187.603 0 0 1 123.996 6.35c3.879-2.265 7.9-4.278 12.038-6.025.703-.3 1.505-.502 1.806-.1.301.402.2 1.004-.502 1.406C104.734 20.509 81.36 56.86 68.92 82.166c-.204.44-.622.744-1.104.803a4.51 4.51 0 0 1-1.605-.1C46.85 79.756.702 71.12.702 71.12a.401.401 0 0 1-.401-.402L0 57.564c0-.302.2-.503.401-.402l61.095 10.142.502-.2 4.213-6.729a24.604 24.604 0 0 0 1.605-2.41l.301-.401c.201-.302 0-.603-.4-.703L28.09 49.43c-.201 0-.301-.201-.301-.402l-.301-11.448c0-.2.2-.401.401-.3l48.756 8.434c.1.1.401-.1.501-.1 1.806-2.21 3.712-4.419 5.217-6.025l1.906-2.109c.401-.502.301-.904-.2-1.004l-26.886-4.82"
                      />
                    </g>
                  </svg>

                  <svg
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 325 85"
                    className={styles.LogoName}
                    aria-hidden="true"
                    focusable="false"
                  >
                    <title>{logoAriaLabel}</title>
                    <path
                      fill="#0080BB"
                      fillRule="nonzero"
                      d="m88.4007993 61.2844489-.3992319 2.4704622h3.4215291l-3.0607774 18.9637831h2.9004433l3.0623807-18.9637831h3.2740217l.4008352-2.4704622zm-8.6147496.0015715h-2.8972366l-3.463216 21.4342453h2.9004434zm-23.3718973-.0015715-3.4632159 21.4342453h2.0009691c5.1900139 0 8.6275763-.4526038 10.2293137-10.3485963 1.7524514-10.8546325-2.098773-11.085649-5.5186987-11.085649zm2.5092281 2.4170298c4.9479094-.2828774 3.7149404 5.3762411 3.1794246 8.6686192-.583616 3.6365454-1.0998917 8.0745767-5.8618135 7.9315665zm-19.5334997-2.4170298-3.4632159 21.4342453h7.8034592l.3976285-2.4736053h-4.9030158l1.1752487-7.276234h4.3819301l.3976285-2.4751768h-4.3787234l1.0902716-6.738767h4.8998092l.3992318-2.4704622zm-22.4852499 21.4342453h2.8972367l1.6963344-10.4868919h.0593236l2.2462803 10.4868919h3.0158839l-2.3440841-10.4334595c2.475558-.4243161 3.5818631-3.4982499 3.9041346-5.5176799.8722173-5.3715265-2.4178378-5.4831059-4.5358509-5.4831059h-3.4776459zm8.3004948-15.3209515c-.3463216 2.1608686-1.3211527 3.5233945-3.4744393 3.3819558l1.1447853-7.0782198h.4633654c2.2126102-.058147 2.1997835 1.6501178 1.8662886 3.696264zm-14.5054233-3.1855133c-.5339124-.2844488-1.10149504-.5374669-1.77008811-.5374669-4.06126198 0-5.02486974 5.7989856-5.45456505 8.4674619-.46176212 2.8460605-1.09187501 8.1877277 2.87799655 8.1877277.63973294 0 1.21212557-.1398671 1.80856831-.4258875l-.40243852 2.5034645c-.79846366.4526038-1.57768724.5924709-2.41783777.5924709-5.91472383 0-5.70147951-6.1620116-4.97516618-10.6550468.69264318-4.291878 2.38416762-11.3449532 8.32775158-11.3449532.98765787 0 1.66106099.1414387 2.42264779.6286163z"
                    />
                    <path
                      fill="#009cde"
                      d="m195.35466 66.258.067217-.0534286 2.427801 16.5141429h2.695067l3.455255-21.4327143h-2.895117l-1.232305 7.645-.691371 8.5862857-.067216.0565714-2.466211-16.2878571h-2.781488l-3.455256 21.4327143h2.896717l1.270715-7.8728572.724979-7.1924285zm-13.142453 5.7435714c.624155-3.8672857 1.717225-11.0015714-3.900165-11.0015714-5.61419 0-6.82569 7.1342857-7.449844 11.0015714-.622554 3.8657143-1.714025 10.9968572 3.901765 10.9968572s6.822489-7.1311429 7.448244-10.9968572zm-8.285251 0c.742584-4.5477143 1.669213-8.327 3.954579-8.327s1.997294 3.7792857 1.264313 8.327c-.737783 4.5492857-1.664412 8.327-3.949778 8.327-2.288566 0-1.997294-3.7777143-1.269114-8.327zm-11.261987-10.7155714h-2.898318l-3.453655 21.4327143h2.895117zm-22.021451 4.972.062415-.0534286 2.434203 16.5141429h2.693466l3.456856-21.4327143h-2.896717l-1.230704 7.645-.689771 8.5862857-.068817.0565714-2.46461-16.2878571h-2.778288l-3.461657 21.4327143h2.896717l1.269114-7.8728572.72498-7.1924285zm-19.059118-4.9735714-2.530226 15.7158571c-.446511 2.7641429.299274 3.3298572 1.488368 3.3298572 1.997295 0 2.368587-1.2492858 2.685465-3.0124286l2.58464-16.0332857h2.895116l-2.453407 15.2067143c-.454512 2.8175714-1.021053 6.5088571-6.057499 6.5088571-4.602739 0-4.513117-3.0674286-3.890563-6.9362857l2.387791-14.7792857z"
                    />
                    <path
                      fill="#009cde"
                      d="m325 5.37663623c0-2.67031964-2.053447-4.79741249-4.64862-4.79741249-2.593575 0-4.64862 2.12709285-4.64862 4.79741249 0 2.67031963 2.055045 4.79577627 4.64862 4.79577627 2.595173 0 4.64862-2.12545664 4.64862-4.79577627zm-1.011542 0c0 2.13854642-1.629974 3.75840943-3.637078 3.75840943-2.003909 0-3.63548-1.61986301-3.63548-3.75840943 0-2.15 1.631571-3.76004567 3.63548-3.76004567 2.007104 0 3.637078 1.61004567 3.637078 3.76004567zm-2.655898.48105022c.861329-.05890411 1.319959-.55304414 1.319959-1.47096651 0-1.31716134-.765449-1.52332572-1.885656-1.52332572h-2.361863v4.97903348h1.01314v-1.98474125h.851741l1.132991 1.98474125h1.109022zm-1.91442-.89174277v-1.20753424h1.123403c.471414 0 1.120208 0 1.120208.54159056 0 .54486301-.329191.66594368-.870918.66594368zm-30.485296 24.76754182-1.270421-1.0226408-1.225676 10.2656774.393112.3239726c2.077417 1.7065829 6.016519 3.6978691 10.55967 3.6978691 9.289251 0 11.684672-7.974962 11.684672-12.9474506 0-4.6566971-1.561259-7.8964231-6.372876-12.8460045-1.642758-1.6902207-2.42898-2.650685-2.818895-3.3395358-.341975-.6054033-.381925-.9964612-.381925-1.6869482 0-1.459513.781428-2.62286914 2.28356-2.62286914 2.828484 0 5.752848 2.27108064 6.414425 2.89284624l1.283205 1.205898 1.32635-10.29676555-.501776-.31742771c-3.127311-1.97165144-6.388856-3.04010654-9.866133-3.04010654-8.038006 0-11.125367 6.60216895-11.125367 11.8217275 0 3.2266363.477806 5.6269787 4.803627 10.6780061.968396 1.1175419 1.761011 2.033828 2.411402 2.8143075.623225.7477549 1.105825 1.3678843 1.470172 1.9274734.674362 1.0291857.888496 1.7671232.888496 2.8093987 0 1.5053273-.605647 3.3951675-2.609556 3.3951675-1.671522 0-3.865594-.893379-7.346066-3.7125952zm-18.918877 12.7314688h8.803454l4.88992-41.93154486h-9.829379l-2.566409 21.93032726-11.158925-21.93032726h-8.846601l-4.840382 41.93154486h9.781439l2.609555-21.9319634zm-46.992772.5334095c11.75019 0 18.944445-10.4652968 18.944445-23.7743531 0-11.83318117-6.920995-19.2240107-16.168697-19.2240107-10.009954 0-19.313586 8.19257991-19.313586 23.8365297 0 14.2106164 9.271672 19.1618341 16.537838 19.1618341zm.142223-10.8596271c-4.098904 0-6.494325-3.8794901-6.494325-8.7718037 0-5.8920472 3.210408-12.5105784 8.755513-12.5105784 3.573158 0 6.355299 3.3395358 6.355299 9.0728691 0 6.5318113-3.440523 12.209513-8.616487 12.209513zm-31.009445-31.60696349-4.837185 41.93318109h9.821388l4.893116-41.93318109zm-31.570347 41.93318109h9.784634l3.744145-31.9047564h10.131403l1.13459-10.02678846h-29.989913l-1.192117 10.02678846h10.08506zm-15.232261-41.93154486h-9.854947l-16.60016 41.93318116h10.166559l3.148086-8.8781583h8.111515l.832565 8.8781583h10.198519zm-6.748409 15.32161336.98917 10.2493151h-4.64063zm-35.172269-15.32324959h-9.4921981l6.9146031 42.46822679h6.331328l16.735991-42.46822679h-10.203314l-7.480299 20.19592849zm-14.2478852 0h-22.5000247l-4.7413049 41.93318109h25.2789695l.9907681-9.0188736h-15.4048464l.8661231-8.0273212h11.0950051l.9907681-9.0221461h-11.0039183l.7622522-6.84105784h12.6738421zm-32.028977 41.93481739 1.0451006-9.0205099h-14.2910313l3.7489388-32.91267126h-9.8773191l-4.742903 41.93318116zm-28.9016653-32.91103504h-12.673842l-.7638503 6.84105784h11.0071144l-.9907681 9.0221461h-11.0982012l-.8629271 8.0273212h15.4016505l-.9891701 9.0188736h-25.2821656l4.74769698-41.93318109h22.49523072z"
                    />
                  </svg>
                </UnstyledLink>
                <div>
                  <div className={styles.navButtonContainer}>
                    <button
                      className={
                        isBusiness && !showBusinessPanel
                          ? styles.navigationButtons
                          : styles.navigationButtonsActive
                      }
                      type="button"
                      onClick={() => onclickPersonal()}
                      data-qa="menu-toggle-personal"
                      ref={personalButtonRef}
                    >
                      Personal
                      <img src={ChevronImg} alt="" />
                    </button>
                    {showPersonalPanel && (
                      <NavigationPanel
                        location={location}
                        pageContext={pageContext}
                        menu={menu}
                        isBusiness={isBusinessPanel}
                        buttonRef={personalButtonRef}
                        onClickOutside={() => setPersonalPanel(false)}
                      />
                    )}
                    <button
                      className={
                        isBusiness && !showPersonalPanel
                          ? styles.navigationButtonsActive
                          : styles.navigationButtons
                      }
                      type="button"
                      onClick={() => onClickBusiness()}
                      data-qa="menu-toggle-business"
                      ref={businessButtonRef}
                    >
                      {t('Header.businessPanelTitle', { defaultValue: 'Business' })}
                      <img src={ChevronImg} alt="" />
                    </button>
                  </div>
                  {showBusinessPanel && (
                    <NavigationPanel
                      location={location}
                      pageContext={pageContext}
                      menu={menu}
                      isBusiness
                      buttonRef={businessButtonRef}
                      onClickOutside={() => setBusinessPanel(false)}
                    />
                  )}
                </div>
                <List className={styles.BannerNavList} unstyled>
                  {/* <List.Item
                    className={classNames(styles.BannerNavItem, styles.WhyElevationsContainer)}
                  >
                    <Link to="/about/why-ecu" className={styles.MainNavLink}>
                      {t('Header.whyElevationsLinkLabel', { defaultValue: 'Why Elevations?' })}
                    </Link>
                  </List.Item> */}

                  <List.Item className={styles.BannerNavItem}>
                    <Button
                      url={`/login${qs.stringify(isBusiness ? { action: 'business' } : {}, {
                        addQueryPrefix: true,
                      })}`}
                      alt={
                        isBusiness
                          ? t('Header.businessLoginButtonAriaLabel', 'Business Log In')
                          : undefined
                      }
                    >
                      <span className={styles.LogInLabel}>
                        {t('Header.loginButtonLabel', { defaultValue: 'Log In' })}
                      </span>
                      <span className={styles.LogInShortLabel}>
                        {t('Header.loginButtonShortLabel', { defaultValue: 'Log In' })}
                      </span>
                    </Button>
                  </List.Item>

                  <List.Item className={classNames(styles.BannerNavItem, styles.LangSwitcher)}>
                    {i18n.language === 'es-US' ? (
                      <Link
                        to={pageContext.pathname}
                        locale="en-US"
                        lang="en-US"
                        alt={t('Header.englishButtonLongLabel', { defaultValue: 'English' })}
                        onMouseDown={() => {
                          window.localStorage.setItem('preferredLanguage', 'en-US')
                        }}
                      >
                        <FontAwesomeIcon
                          aria-hidden
                          aria-label={t('Header.globeIconAriaLabel', {
                            defaultValue: 'Globe icon',
                          })}
                          icon={faGlobe}
                          className={styles.LinkIcon}
                        />
                        <span className={styles.LangSwitcherLabelLong}>
                          {t('Header.englishButtonLongLabel', { defaultValue: 'English' })}
                        </span>
                        <span className={styles.LangSwitcherLabelShort}>
                          {t('Header.englishButtonShortLabel', { defaultValue: 'EN' })}
                        </span>
                      </Link>
                    ) : (
                      <Link
                        to={pageContext.pathname}
                        locale="es-US"
                        lang="es-US"
                        alt={t('Header.spanishButtonLongLabel', { defaultValue: 'Español' })}
                        onMouseDown={() => {
                          window.localStorage.setItem('preferredLanguage', 'es-US')
                        }}
                      >
                        <FontAwesomeIcon
                          aria-hidden
                          aria-label={t('Header.globeIconAriaLabel', {
                            defaultValue: 'Globe icon',
                          })}
                          icon={faGlobe}
                          className={styles.LinkIcon}
                        />
                        <span className={styles.LangSwitcherLabelLong}>
                          {t('Header.spanishButtonLongLabel', { defaultValue: 'Español' })}
                        </span>
                        <span className={styles.LangSwitcherLabelShort}>
                          {t('Header.spanishButtonShortLabel', { defaultValue: 'ES' })}
                        </span>
                      </Link>
                    )}
                  </List.Item>

                  <List.Item
                    className={classNames(styles.BannerNavItem, styles.SearchToggleContainer)}
                  >
                    <button type="button" className={styles.SearchToggle} onClick={openSearchMenu}>
                      {i18n.language === 'en-US' ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={42}
                          height={40}
                          viewBox="0 0 42 40"
                        >
                          <g fill="#333">
                            <path d="M26.294 26.579h-.903l-.32-.31c1.12-1.303 1.795-2.995 1.795-4.836 0-4.105-3.328-7.433-7.433-7.433S12 17.328 12 21.433s3.328 7.433 7.433 7.433c1.84 0 3.533-.675 4.837-1.796l.309.32v.904L30.296 34 32 32.296l-5.706-5.717zm-6.861 0c-2.848 0-5.146-2.299-5.146-5.146 0-2.848 2.298-5.146 5.146-5.146 2.847 0 5.146 2.298 5.146 5.146 0 2.847-2.299 5.146-5.146 5.146zM2.89 8C4.536 8 5.56 6.87 5.56 5.626c0-1.119-.72-1.763-2.006-2.057l-1.156-.26c-.654-.147-.992-.427-.992-1.003 0-.746.621-1.334 1.494-1.334 1.079 0 1.548.791 1.635 1.368l.96-.35C5.32 1.118 4.623 0 2.879 0 1.45 0 .327 1.13.327 2.385c0 1.198.785 1.783 1.887 2.032l1.1.249c.775.17 1.157.474 1.157 1.05 0 .702-.524 1.278-1.592 1.278-1.167 0-1.778-.825-1.854-1.718L0 5.616C.142 6.755 1.025 8 2.89 8zm8.389 0v-.828H7.648V4.375h3.293v-.823H7.648V.828h3.63V0H6.58v8h4.7zm2.163 0l.818-2.234h3.424L18.524 8h1.155l-3.075-8h-1.232l-3.075 8h1.145zm10.37-8c1.461 0 2.366 1.06 2.366 2.38 0 1.152-.709 2.043-1.876 2.269L26.145 8h-1.243l-1.766-3.272H21.98V8H20.9V0zM15.96 1l1.341 4h-2.66l1.319-4zm7.655 0H21.98v3h1.636c.872 0 1.46-.58 1.46-1.481 0-.926-.588-1.519-1.46-1.519zm7.48 7c1.777 0 2.998-1.07 3.413-2.57l-.949-.36c-.316 1.093-1.145 1.916-2.464 1.916-1.407 0-2.748-.87-2.748-2.955s1.341-3.017 2.748-3.017c1.265 0 2.115.677 2.41 1.883l.992-.361C34.105.992 32.862 0 31.096 0c-1.92 0-3.838 1.36-3.838 4.03C27.258 6.703 29.1 8 31.096 8zm5.888 0V4.446h3.947V8H42V0h-1.069v3.419h-3.947V0h-1.069v8h1.07z" />
                          </g>
                        </svg>
                      ) : (
                        <svg
                          width={42}
                          height={40}
                          viewBox="0 0 42 40"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g fill="none" fillRule="nonzero">
                            <path
                              d="M26.3 26.58h-.9l-.33-.31a7.43 7.43 0 10-.8.8l.3.32v.9L30.3 34l1.7-1.7-5.7-5.72zm-6.87 0a5.14 5.14 0 11-.01-10.28 5.14 5.14 0 01.01 10.28z"
                              fill="#333"
                            />
                            <path
                              d="M2.23 7.9c1.17 0 1.97-.73 1.97-1.74 0-.7-.42-1.35-1.15-1.53.56-.21.96-.7.96-1.38 0-1-.72-1.73-1.93-1.73H0V7.9h2.23zm-.25-3.59H.88V2.3h1.06c.73 0 1.17.4 1.17 1 0 .59-.42 1.01-1.13 1.01zm.13 2.82H.88V5.07h1.26c.78 0 1.16.43 1.16 1.04 0 .62-.48 1.02-1.2 1.02zm5.07-6.06L8.41 0H7.24l-.9 1.07h.84zm-.2 6.97c1.3 0 2.3-.81 2.3-2.32v-4.2H8.4V5.7c0 .97-.52 1.52-1.42 1.52-.89 0-1.42-.55-1.42-1.52V1.52H4.7v4.2c0 1.5 1 2.32 2.3 2.32zm5 0c1.36 0 2.21-.9 2.21-1.9 0-.88-.6-1.55-1.66-1.78l-.95-.21c-.54-.12-.82-.47-.82-.93 0-.6.51-1.06 1.23-1.06.9 0 1.28.63 1.35 1.09l.8-.28c-.15-.7-.72-1.58-2.16-1.58-1.18 0-2.1.9-2.1 1.9 0 .95.64 1.54 1.55 1.74l.9.2c.65.13.96.53.96.99 0 .56-.43 1.02-1.31 1.02-.97 0-1.47-.66-1.53-1.37l-.85.27c.12.9.85 1.9 2.39 1.9zm7.93.4l.6-.54-.74-.83c.54-.56.9-1.36.9-2.36 0-2.14-1.6-3.32-3.2-3.32s-3.2 1.18-3.2 3.32c0 2.13 1.6 3.33 3.2 3.33.58 0 1.17-.16 1.67-.46l.77.85zm-2.44-1.21c-1.2 0-2.3-.86-2.3-2.52 0-1.67 1.1-2.51 2.3-2.51 1.18 0 2.3.84 2.3 2.5 0 .75-.23 1.32-.57 1.73L18.1 5.18l-.62.53 1.11 1.24c-.34.19-.72.28-1.11.28zm5.93.8c1.3 0 2.29-.8 2.29-2.3v-4.2h-.88V5.7c0 .97-.51 1.52-1.41 1.52-.9 0-1.43-.55-1.43-1.52V1.52h-.87v4.2c0 1.5 1 2.32 2.3 2.32zm7.02-.13v-.82h-3V5.13h2.72V4.3h-2.71V2.34h3v-.82h-3.89V7.9h3.88zm2.76 0c1.62 0 3.06-1.09 3.06-3.18 0-2.1-1.42-3.2-3.04-3.2h-2.23V7.9h2.21zm-.02-.78h-1.3V2.3h1.32c1.17 0 2.15.81 2.15 2.42 0 1.6-1 2.4-2.17 2.4zm3.6.78l.67-1.78h2.83l.7 1.78h.94l-2.53-6.38h-1.02L35.81 7.9h.95zm3.18-2.6h-2.2l1.1-2.86 1.1 2.86z"
                              fill="#333"
                            />
                          </g>
                        </svg>
                      )}

                      <ScreenReaderText>
                        {t('Header.searchMenuButtonAriaLabel', {
                          defaultValue: 'Open Search Menu',
                        })}
                      </ScreenReaderText>
                    </button>
                  </List.Item>

                  <List.Item className={styles.BannerNavItem}>
                    <button
                      type="button"
                      className={styles.MenuToggle}
                      onClick={openHamburgerMenu}
                      data-qa="menu-toggle"
                    >
                      {i18n.language === 'en-US' ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={32}
                          height={34}
                          role="img"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path
                            d="M31.571 18c.237 0 .429-.299.429-.667v-2.666c0-.368-.192-.667-.429-.667H.43c-.237 0-.429.299-.429.667v2.666c0 .368.192.667.429.667h31.14zm-7.892 8c.177 0 .321-.299.321-.667v-2.666c0-.368-.144-.667-.321-.667H.32c-.177 0-.321.299-.321.667v2.666c0 .368.144.667.321.667h23.36zm-7.893 8c.118 0 .214-.299.214-.667v-2.666c0-.368-.096-.667-.214-.667H.214c-.118 0-.214.299-.214.667v2.666c0 .368.096.667.214.667h15.572zM1.075 7.892V4.818l-.107-3.3 2.67 6.374h.824l2.676-6.39-.101 3.316v3.074h1.075V0H6.718L4.053 6.439 1.388 0H0v7.892h1.075zm14.634 0V7.04h-4.104V4.244h3.533v-.851h-3.533V.856h4.048V0H10.53v7.892h5.179zm2.94 0V1.81l4.103 6.082h1.08V0h-1.07v6.108L18.649 0h-1.08v7.892h1.08zM29.038 8l.285-.01c.821-.059 1.471-.324 1.951-.798.48-.473.721-1.082.725-1.826V0h-1.08v5.35c0 .57-.162 1.013-.485 1.328-.322.314-.788.471-1.396.471-.601 0-1.064-.158-1.389-.474-.324-.316-.487-.76-.487-1.33V0H26.1v5.377c.007.816.276 1.458.806 1.924.53.466 1.24.699 2.133.699z"
                            fill="#333"
                          />
                        </svg>
                      ) : (
                        <svg
                          width={34}
                          height={40}
                          viewBox="0 0 34 40"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g fill="none" fillRule="evenodd">
                            <path
                              d="M16.79 30c.11 0 .21.3.21.67v2.66c0 .37-.1.67-.21.67H1.2c-.11 0-.21-.3-.21-.67v-2.66c0-.37.1-.67.21-.67zm7.89-8c.18 0 .32.3.32.67v2.66c0 .37-.14.67-.32.67H1.32c-.18 0-.32-.3-.32-.67v-2.66c0-.37.14-.67.32-.67zm7.9-8c.23 0 .42.3.42.67v2.66c0 .37-.2.67-.43.67H1.43c-.24 0-.43-.3-.43-.67v-2.66c0-.37.2-.67.43-.67zM28.15 2v5.53c.04.48.2.86.49 1.14.32.32.79.48 1.39.48.6 0 1.07-.16 1.4-.47.32-.32.48-.76.48-1.33V2H33v5.59a2.42 2.42 0 01-.72 1.6c-.48.48-1.13.74-1.96.8l-.28.01c-.9 0-1.6-.23-2.13-.7-.53-.47-.8-1.1-.81-1.92V2h1.06zM2.4 2l2.66 6.44L7.72 2h1.4v7.9H8.03V6.81l.1-3.32-2.68 6.4h-.82L1.97 3.51l.1 3.3v3.07H1V2h1.39zm14.26 0v.86h-4.04v2.53h3.53v.85H12.6v2.8h4.1v.85h-5.18V2h5.12zm3 0l4.11 6.1V2h1.07v7.9h-1.08l-4.1-6.09v6.08h-1.08V2h1.08z"
                              fill="#333"
                            />
                            <path fill="#333" d="M29.85 2.07L31.4.73h-1.17L29 2.07z" />
                          </g>
                        </svg>
                      )}
                      <ScreenReaderText>
                        {t('Header.primaryMenuButtonAriaLabel', {
                          defaultValue: 'Open Primary Menu',
                        })}
                      </ScreenReaderText>
                    </button>
                  </List.Item>
                </List>
              </Stack>
            </Col>
          </Row>
        </Container>
      </header>

      <HamburgerMenu
        location={location}
        pageContext={pageContext}
        menu={menu}
        open={menuOpen}
        close={closeMenu}
      />

      {currentPage && shouldShowSubnav && (
        <SubNav
          showSubnav={showSubnav}
          showSubnavOnScroll={showSubnavOnScroll}
          isBusiness={isBusiness}
          menu={menu}
          pageContext={pageContext}
          location={location}
        />
      )}
    </Fragment>
  )
}

Header.propTypes = {
  location: PropTypes.object,
  pageContext: PropTypes.object,
  menu: PropTypes.object,
  showSubnav: PropTypes.bool,
  showSubnavOnScroll: PropTypes.bool,
}

export { Header }
export default Header
